/**
 * Hook for tracking background job status with automatic polling.
 */

import { useState, useEffect, useCallback } from "react";
import { getJobStatus } from "@wayak/ai/sdk";

interface JobStatus {
  id: string;
  job_type: string;
  status: "queued" | "running" | "completed" | "failed" | "cancelled" | "paused";
  phase: string | null;
  progress_percentage: number;
  items_processed: number;
  items_total: number | null;
  estimated_completion: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  result: any | null;
}

interface UseJobStatusOptions {
  jobId: string | null;
  pollInterval?: number; // Default: 2000ms (2 seconds)
  enabled?: boolean;
  onComplete?: (result: any) => void;
  onError?: (error: string) => void;
}

export function useJobStatus(options: UseJobStatusOptions) {
  const {
    jobId,
    pollInterval = 2000,
    enabled = true,
    onComplete,
    onError,
  } = options;

  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchJobStatus = useCallback(async () => {
    if (!jobId || !enabled) return;

    try {
      setIsLoading(true);

      const response = await getJobStatus({
        path: {
          job_id: jobId,
        },
      });

      if (response.error) {
        throw new Error(response.error.detail || "Failed to fetch job status");
      }

      const status = response.data as JobStatus;
      setJobStatus(status);
      setError(null);

      // Trigger callbacks based on status
      if (status.status === "completed" && onComplete && status.result) {
        onComplete(status.result);
      } else if (status.status === "failed" && onError && status.error) {
        onError(status.error);
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [jobId, enabled, onComplete, onError]);

  // Poll for status updates
  useEffect(() => {
    if (!jobId || !enabled) return;

    // Initial fetch
    fetchJobStatus();

    // Only continue polling if job is not in terminal state
    if (
      jobStatus &&
      (jobStatus.status === "completed" ||
        jobStatus.status === "failed" ||
        jobStatus.status === "cancelled")
    ) {
      return;
    }

    // Set up polling interval
    const interval = setInterval(() => {
      fetchJobStatus();
    }, pollInterval);

    return () => {
      clearInterval(interval);
    };
  }, [jobId, enabled, pollInterval, jobStatus?.status, fetchJobStatus]);

  const refresh = useCallback(() => {
    fetchJobStatus();
  }, [fetchJobStatus]);

  return {
    jobStatus,
    isLoading,
    error,
    refresh,
    isCompleted: jobStatus?.status === "completed",
    isFailed: jobStatus?.status === "failed",
    isRunning: jobStatus?.status === "running" || jobStatus?.status === "queued",
  };
}
